import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { FullScreenLoader } from '@/components/ui/full-screen-loader';
import { useAuth } from '@/hooks/use-auth';
import { ssoComplete } from '@/lib/api/auth';
import { ApiError } from '@/lib/api/client';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useLocation } from 'wouter';
export function parseHandoffFromHash(hash) {
    // hash arrives as e.g. "#handoff=tok" or "#error=access_denied"
    const params = new URLSearchParams(hash.startsWith('#') ? hash.slice(1) : hash);
    const handoffToken = params.get('handoff');
    const errorReason = params.get('error'); // null when not present
    return { handoffToken, errorReason };
}
/**
 * Landing page for the SSO callback redirect.
 *
 * The ControlPlane callback handler redirects the browser here after a
 * successful OIDC round-trip:
 *   /auth/sso/finish#handoff=<short-lived-token>
 *
 * On error (e.g. the IdP rejected the request) it may redirect here with:
 *   /auth/sso/finish#error=<reason>
 *
 * This page exchanges the handoff token for a real session via
 * POST /auth/sso/complete and then calls establishSession() — the same
 * primitive used by AcceptInvitation and MfaChallenge.
 *
 * StrictMode safety: the hash is parsed ONCE via a lazy useState initializer
 * so the token is captured before the hash-clear effect runs. The effect
 * therefore reads `parsed`, not `window.location.hash`, and is idempotent on
 * the StrictMode remount.
 */
export default function SsoFinish() {
    const { t } = useTranslation();
    const { establishSession } = useAuth();
    const [, setLocation] = useLocation();
    // Parse the hash ONCE at render time (lazy initializer) before the effect
    // clears it, so StrictMode's mount→cleanup→remount cycle sees the same token.
    const [parsed] = useState(() => parseHandoffFromHash(window.location.hash));
    const [state, setState] = useState({ kind: 'loading' });
    // The handoff token is single-use (the server DELETEs it on /complete). A plain `cancelled` flag can't
    // survive StrictMode's mount→cleanup→remount, so the second mount would fire a SECOND /complete with
    // the same token and 401 on the already-consumed row — surfacing as a spurious "session expired" error
    // even though the first call succeeded. This ref persists across the remount, so /complete runs once.
    const ranRef = useRef(false);
    useEffect(() => {
        const { handoffToken, errorReason } = parsed;
        if (errorReason !== null || !handoffToken) {
            const message = errorReason === 'browser_mismatch'
                ? t('sso.finish.browserMismatch')
                : errorReason
                    ? t('sso.finish.errorWithReason', { reason: errorReason })
                    : t('sso.finish.error');
            setState({ kind: 'error', message }); // eslint-disable-line react-hooks/set-state-in-effect -- one-shot mount transition: loading → error
            return;
        }
        if (ranRef.current)
            return;
        ranRef.current = true;
        // Clear the hash immediately so the token doesn't sit in history
        window.history.replaceState(null, '', window.location.pathname + window.location.search);
        void (async () => {
            try {
                const session = await ssoComplete(handoffToken);
                establishSession({
                    email: session.email ?? '',
                    accessToken: session.accessToken,
                    csrfToken: session.csrfToken,
                });
                setLocation('/');
            }
            catch (err) {
                const status = err instanceof ApiError ? err.status : 0;
                const message = status === 401 || status === 410 ? t('sso.finish.tokenExpired') : t('sso.finish.error');
                setState({ kind: 'error', message });
            }
        })();
        // establishSession and setLocation are stable refs; t changes only on lang switch
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);
    if (state.kind === 'loading') {
        return _jsx(FullScreenLoader, {});
    }
    return (_jsx("div", { className: "bg-background flex min-h-dvh items-center justify-center p-4", children: _jsxs(Card, { className: "w-full max-w-md", children: [_jsx(CardHeader, { className: "text-center", children: _jsx(CardTitle, { className: "text-2xl font-bold", children: t('sso.finish.errorTitle') }) }), _jsxs(CardContent, { className: "space-y-4 text-center", children: [_jsx("p", { className: "text-muted-foreground text-sm", children: state.message }), _jsx(Button, { asChild: true, variant: "outline", className: "w-full", children: _jsx(Link, { href: "/login", children: t('sso.finish.backToLogin') }) })] })] }) }));
}
